import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Modal, Spinner} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {calibrationRequest, saveCameraCalibration} from '../api/camera-calibration';
import './partner-camera-calibration.css';

const terminal = ['READY', 'FAILED', 'EXPIRED', 'CANCELLED'];
export function polygonViewport(width, height, points = []) {
  const xs = [0, width, ...points.map(p => p[0])], ys = [0, height, ...points.map(p => p[1])];
  const margin = Math.max(width, height) * .04;
  const x = Math.min(...xs) - margin, y = Math.min(...ys) - margin;
  return [x, y, Math.max(...xs) - x + margin, Math.max(...ys) - y + margin];
}
export function sourcePoint(clientX, clientY, rect, box) {
  const scale = Math.min(rect.width / box[2], rect.height / box[3]);
  if (!scale) return null;
  return [box[0] + (clientX - rect.left - (rect.width - box[2] * scale) / 2) / scale,
    box[1] + (clientY - rect.top - (rect.height - box[3] * scale) / 2) / scale];
}

export default function PartnerCameraCalibration({fieldId, token, onHide}) {
  const {t} = useTranslation();
  const [catalog, setCatalog] = useState(null), [camera, setCamera] = useState(null);
  const [snapshot, setSnapshot] = useState(null), [imageUrl, setImageUrl] = useState(null);
  const [points, setPoints] = useState([]), [viewport, setViewport] = useState([0, 0, 1, 1]);
  const [error, setError] = useState(null), [requesting, setRequesting] = useState(false);
  const [saving, setSaving] = useState(false), [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);
  const controller = useRef(null), mounted = useRef(true), drag = useRef(null);
  const svg = useRef(null);
  const busy = requesting || saving || (snapshot && !terminal.includes(snapshot.state));
  const editable = camera && camera.type !== 'bullet' && snapshot && snapshot.values && !snapshot.savedRevision && imageUrl && !busy;

  useEffect(() => () => {mounted.current = false; if (controller.current) controller.current.abort();}, []);
  useEffect(() => {
    const abort = new AbortController();
    calibrationRequest(fieldId, token, '', {signal: abort.signal}).then(data => {
      setCatalog(data);
      if (data.cameras.length === 1) setCamera(data.cameras[0]);
    }).catch(e => {if (e.name !== 'AbortError') setError(e.message);});
    return () => abort.abort();
  }, [fieldId, token]);

  async function capture(selected) {
    if (controller.current) controller.current.abort();
    const abort = new AbortController(); controller.current = abort;
    setError(null); setRequesting(true); setSnapshot(null); setImageUrl(null); setDirty(false);
    try {
      const result = await calibrationRequest(fieldId, token, '/snapshots', {
        method: 'POST', body: JSON.stringify({cameraKey: selected.key}), signal: abort.signal
      });
      if (!abort.signal.aborted) setSnapshot(result);
    } catch (e) {if (!abort.signal.aborted) setError(e.message);}
    finally {if (!abort.signal.aborted) setRequesting(false);}
  }
  useEffect(() => {
    if (camera && catalog && catalog.available && catalog.online && catalog.inSync && camera.canCapture !== false) capture(camera);
    // A new camera selection triggers exactly one capture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera]);

  useEffect(() => {
    if (!snapshot || terminal.includes(snapshot.state)) return undefined;
    const abort = new AbortController();
    const timer = setTimeout(() => {
      calibrationRequest(fieldId, token, `/snapshots/${snapshot.id}`, {signal: abort.signal})
        .then(setSnapshot).catch(e => {if (e.name !== 'AbortError') {setError(e.message); setSnapshot(s => ({...s, state: 'FAILED'}));}});
    }, 1500);
    return () => {clearTimeout(timer); abort.abort();};
  }, [snapshot, fieldId, token]);

  useEffect(() => {
    if (!snapshot || snapshot.state !== 'READY') return undefined;
    const abort = new AbortController(); let url;
    calibrationRequest(fieldId, token, `/snapshots/${snapshot.id}/image`, {signal: abort.signal}).then(blob => {
      if (abort.signal.aborted) return;
      url = URL.createObjectURL(blob); setImageUrl(url);
      const polygon = (snapshot.savedValues || snapshot.values || {}).fieldPolygon || [];
      setPoints(polygon.map(p => [...p])); setViewport(polygonViewport(snapshot.width, snapshot.height, polygon));
    }).catch(e => {if (e.name !== 'AbortError') setError(e.message);});
    return () => {abort.abort(); if (url) URL.revokeObjectURL(url);};
    // Download only when the photograph becomes ready, not when save status changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot && snapshot.id, snapshot && snapshot.state, fieldId, token]);

  function discard() {return !dirty || window.confirm(t('CAMERA_LIVE_DISCARD'));}
  function close() {if (!saving && discard()) onHide();}
  function refresh() {if (discard()) {setSaved(false); capture(camera);}}
  async function save() {
    setSaving(true); setError(null); setSaved(false);
    const abort = new AbortController(); controller.current = abort;
    try {
      let result = await saveCameraCalibration(fieldId, token, snapshot, {...snapshot.values, fieldPolygon: points});
      if (mounted.current) setSnapshot(result);
      const deadline = Date.now() + 120000;
      while (mounted.current && result.applicationState !== 'APPLIED') {
        if (['SUPERSEDED', 'REJECTED'].includes(result.applicationState)) throw new Error('revision_conflict');
        if (Date.now() > deadline) throw new Error('application_pending');
        await new Promise(resolve => setTimeout(resolve, 1500));
        if (!mounted.current) return;
        result = await calibrationRequest(fieldId, token, `/snapshots/${snapshot.id}`, {signal: abort.signal});
      }
      if (!mounted.current) return;
      setDirty(false); setSaved(true);
      // Each accepted revision needs a fresh snapshot before another edit/save.
      await capture(camera);
    } catch (e) {if (mounted.current && e.name !== 'AbortError') setError(e.message);}
    finally {if (mounted.current) setSaving(false);}
  }
  function move(e) {
    if (drag.current === null || !editable) return;
    const point = sourcePoint(e.clientX, e.clientY, svg.current.getBoundingClientRect(), viewport);
    if (!point) return;
    const index = drag.current;
    setPoints(current => current.map((p, i) => i === index ? point.map(n => Math.round(n * 1000) / 1000) : p));
    setDirty(true); setSaved(false);
  }
  function release(e) {
    drag.current = null;
    if (svg.current && svg.current.hasPointerCapture && svg.current.hasPointerCapture(e.pointerId)) svg.current.releasePointerCapture(e.pointerId);
  }
  const message = error ? t(`CAMERA_CALIBRATION_ERROR_${error}`, {defaultValue: t('CAMERA_CALIBRATION_FAILED')}) :
    saving ? t('CAMERA_LIVE_SAVING') : saved ? t('CAMERA_LIVE_SAVED') :
    dirty ? t('CAMERA_LIVE_UNSAVED') : camera && camera.type !== 'bullet' ? t('CAMERA_LIVE_DRAG') : '';
  const radius = Math.max(viewport[2] / 140, 8);
  return <Modal show onHide={close} dialogClassName={camera ? 'camera-live-dialog' : ''} backdrop="static" keyboard={!saving} aria-labelledby="camera-live-title">
    {!camera ? <>
      <Modal.Header closeButton><Modal.Title id="camera-live-title">{t('CAMERA_CALIBRATION')}</Modal.Title></Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{message}</Alert>}
        {!catalog && !error && <Spinner animation="border"/>}
        {catalog && !catalog.available && <Alert variant="info">{t('CAMERA_CALIBRATION_UPGRADE')}</Alert>}
        {catalog && catalog.available && !catalog.cameras.length && <Alert variant="info">{t('CAMERA_CALIBRATION_NO_CAMERAS')}</Alert>}
        {catalog && catalog.cameras.length > 1 && <div className="camera-live-choices">
          <p>{t('CAMERA_LIVE_CHOOSE')}</p>
          {catalog.cameras.map(c => <Button key={c.key} variant="outline-primary" onClick={() => setCamera(c)}>{c.name || c.key} <small>{c.type === 'bullet' ? 'Bullet' : t('CAMERA_LIVE_PANO')}</small></Button>)}
        </div>}
      </Modal.Body>
    </> : <div className="camera-live-shell">
      <header className="camera-live-toolbar">
        <div><strong id="camera-live-title">{t('CAMERA_CALIBRATION')}</strong><span>{camera.name || camera.key}</span></div>
        <nav aria-label={t('CAMERA_CALIBRATION')}>
          <Button variant="outline-light" disabled={saving} onClick={close}>{t('CLOSE')}</Button>
          <Button variant="primary" disabled={!editable || !dirty || snapshot.savedRevision} onClick={save}>{t('SAVE')}</Button>
          <Button variant="outline-light" disabled={busy || !catalog.online || !catalog.inSync || camera.canCapture === false} onClick={refresh}>{t('CAMERA_LIVE_REFRESH')}</Button>
          <Button variant="outline-light" disabled title={t('CAMERA_CALIBRATION_PLAYER_LATER')}>{t('CAMERA_LIVE_PLAYER')}</Button>
        </nav>
      </header>
      <div className="camera-live-status" role="status">
        {message}
        {!catalog.online && <span>{t('CAMERA_CALIBRATION_OFFLINE')}</span>}
        {!catalog.inSync && <span>{t('CAMERA_CALIBRATION_SYNC')}</span>}
        {camera.canCapture === false && <span>{t('CAMERA_CALIBRATION_UPGRADE')}</span>}
        {snapshot && ['FAILED', 'EXPIRED', 'CANCELLED'].includes(snapshot.state) && <span>{t('CAMERA_CALIBRATION_FAILED')}</span>}
      </div>
      <main className="camera-live-canvas">
        {busy && <div className="camera-live-loading"><Spinner animation="border"/><span>{saving ? t('CAMERA_LIVE_SAVING') : t('CAMERA_CALIBRATION_CAPTURING')}</span></div>}
        {imageUrl && snapshot && <svg ref={svg} viewBox={viewport.join(' ')} preserveAspectRatio="xMidYMid meet" role="img" aria-label={t('CAMERA_CALIBRATION_PHOTO')} onPointerMove={move} onPointerUp={release} onPointerCancel={release}>
          <image href={imageUrl} x="0" y="0" width={snapshot.width} height={snapshot.height}/>
          {points.length > 0 && <polygon points={points.map(p => p.join(',')).join(' ')} fill="rgba(39,211,174,.08)" stroke="#32efc1" strokeWidth="2" vectorEffect="non-scaling-stroke"/>}
          {points.map((p, i) => <g key={i}>
            <circle data-testid={`polygon-point-${i}`} cx={p[0]} cy={p[1]} r={radius} fill="#092e2a" stroke="#7bffe0" strokeWidth="2" vectorEffect="non-scaling-stroke" style={{cursor: editable ? 'grab' : 'default'}} onPointerDown={e => {
              if (!editable || e.button !== 0) return;
              e.preventDefault(); drag.current = i; svg.current.setPointerCapture(e.pointerId);
            }}><title>{i + 1}: {p[0]}, {p[1]}</title></circle>
            <text x={p[0]} y={p[1]} textAnchor="middle" dominantBaseline="central" fontSize={radius} fill="white" pointerEvents="none">{i + 1}</text>
          </g>)}
        </svg>}
      </main>
    </div>}
  </Modal>;
}
